// src/services/photoUploadExtensionService.js
// Service for photo upload tier extensions and upgrades with Cashfree payment integration

import axios from 'axios';
import supabase from './supabase';

const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL ||
  'https://asia-south1-invits-app.cloudfunctions.net';

/**
 * Get authorization header for API calls
 */
const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Calculate GST (18% fixed)
 */
export const calculateGST = (baseAmount) => {
  const gstRate = 18;
  const gstAmount = parseFloat(((baseAmount * gstRate) / 100).toFixed(2));
  const total = parseFloat((baseAmount + gstAmount).toFixed(2));
  return {
    base: baseAmount,
    gst: gstAmount,
    total: total,
    gstRate,
  };
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount) => {
  return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Get photo upload extension pricing for all tiers
 */
export const getPhotoUploadExtensionPricing = async () => {
  try {
    const { data, error } = await supabase
      .from('photo_upload_extension_pricing')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      pricing: data || [],
    };
  } catch (error) {
    console.error('Error fetching photo upload extension pricing:', error);
    return {
      success: false,
      error: error.message,
      pricing: [],
    };
  }
};

/**
 * Get event's current photo upload status including expiry
 * @param {string} eventId - Event ID
 * @param {string} financialYear - Financial year
 */
export const getEventPhotoUploadStatus = async (eventId, financialYear) => {
  try {
    const { data, error } = await supabase
      .rpc('get_event_photo_upload_status', {
        p_event_id: eventId,
        p_financial_year: financialYear,
      });

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error('Event not found');
    }

    const status = data[0];

    return {
      success: true,
      status: {
        eventId: status.event_id,
        currentTierId: status.photo_pricing_tier_id,
        tierName: status.tier_name,
        tierDisplayName: status.tier_display_name,
        photoLimit: status.photo_upload_limit,
        photosCount: status.photos_count,
        expiresAt: status.photo_upload_expires_at,
        activatedAt: status.photo_upload_activated_at,
        isExpired: status.is_expired,
        daysUntilExpiry: status.days_until_expiry,
        extensionPrice: status.extension_price,
        canExtend: status.can_extend,
        canUpgrade: status.can_upgrade,
        nextTierId: status.next_tier_id,
        nextTierDisplayName: status.next_tier_display_name,
        nextTierPrice: status.next_tier_price,
      },
    };
  } catch (error) {
    console.error('Error fetching event photo upload status:', error);
    return {
      success: false,
      error: error.message,
      status: null,
    };
  }
};

/**
 * Get expiry status badge info
 * @param {number} daysUntilExpiry - Days until expiry (negative if expired)
 * @param {boolean} isExpired - Whether tier is expired
 */
export const getExpiryStatusBadge = (daysUntilExpiry, isExpired) => {
  if (isExpired) {
    return {
      label: 'Expired',
      color: '#ef4444', // red
      variant: 'expired',
    };
  }

  if (daysUntilExpiry === null || daysUntilExpiry === undefined) {
    return {
      label: 'Lifetime',
      color: '#10b981', // green
      variant: 'lifetime',
    };
  }

  if (daysUntilExpiry <= 7) {
    return {
      label: `${daysUntilExpiry} days left`,
      color: '#ef4444', // red
      variant: 'critical',
    };
  }

  if (daysUntilExpiry <= 30) {
    return {
      label: `${daysUntilExpiry} days left`,
      color: '#f59e0b', // orange
      variant: 'warning',
    };
  }

  return {
    label: `${daysUntilExpiry} days left`,
    color: '#10b981', // green
    variant: 'active',
  };
};

/**
 * Create a photo upload extension/upgrade order via Cashfree
 * @param {Object} params - Order parameters
 * @returns {Promise<Object>} Order details with payment session ID
 */
export const createPhotoUploadExtensionOrder = async (params) => {
  try {
    const {
      eventId,
      financialYear,
      purchaseType, // 'extension' or 'upgrade'
      tierId,
      customerName,
      customerEmail,
      customerPhone,
      customerState,
    } = params;

    // Validation
    if (!eventId || !financialYear || !purchaseType || !tierId) {
      throw new Error('Missing required fields');
    }

    if (!['extension', 'upgrade'].includes(purchaseType)) {
      throw new Error('Invalid purchase type. Must be "extension" or "upgrade"');
    }

    if (!customerName || !customerEmail || !customerPhone) {
      throw new Error('Missing customer details');
    }

    const headers = await getAuthHeader();

    console.log('Creating photo upload extension order:', {
      eventId,
      financialYear,
      purchaseType,
      tierId,
    });

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/createPhotoUploadExtensionOrderCashfree`,
      {
        eventId,
        financialYear,
        purchaseType,
        tierId,
        customerName,
        customerEmail,
        customerPhone,
        customerState,
      },
      { headers }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create order');
    }

    return {
      success: true,
      orderId: response.data.orderId,
      purchaseId: response.data.purchaseId,
      paymentSessionId: response.data.paymentSessionId,
      baseAmount: response.data.baseAmount,
      gstAmount: response.data.gstAmount,
      totalAmount: response.data.totalAmount,
      tierName: response.data.tierName,
      displayName: response.data.displayName,
      photoLimit: response.data.photoLimit,
      newExpiryDate: response.data.newExpiryDate,
    };
  } catch (error) {
    console.error('Error creating photo upload extension order:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to create order',
    };
  }
};

/**
 * Verify photo upload extension/upgrade payment
 * @param {string} orderId - Cashfree order ID
 * @returns {Promise<Object>} Payment verification result
 */
export const verifyPhotoUploadExtensionPayment = async (orderId) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const headers = await getAuthHeader();

    console.log('Verifying photo upload extension payment:', orderId);

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/verifyPhotoUploadExtensionPaymentCashfree`,
      { orderId },
      { headers }
    );

    if (!response.data.success) {
      return {
        success: false,
        paymentStatus: response.data.paymentStatus || 'failed',
        error: response.data.error || 'Payment verification failed',
      };
    }

    return {
      success: true,
      paymentStatus: response.data.paymentStatus,
      purchaseId: response.data.purchaseId,
      tierId: response.data.tierId,
      purchaseType: response.data.purchaseType,
      invoiceId: response.data.invoiceId,
      invoiceNumber: response.data.invoiceNumber,
      newExpiryDate: response.data.newExpiryDate,
      photoLimit: response.data.photoLimit,
    };
  } catch (error) {
    console.error('Error verifying photo upload extension payment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Payment verification failed',
      paymentStatus: 'error',
    };
  }
};

/**
 * Get photo upload extension purchase history for an event
 * @param {string} eventId - Event ID
 */
export const getPhotoUploadExtensionHistory = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('photo_upload_extension_purchases')
      .select(`
        *,
        pricing:photo_upload_extension_pricing!tier_id(
          tier_name,
          display_name,
          photo_limit
        )
      `)
      .eq('event_id', eventId)
      .eq('payment_status', 'completed')
      .order('purchased_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      history: data || [],
    };
  } catch (error) {
    console.error('Error fetching photo upload extension history:', error);
    return {
      success: false,
      error: error.message,
      history: [],
    };
  }
};

/**
 * Get all photo upload extension purchases for current user
 */
export const getUserPhotoUploadExtensions = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('photo_upload_extension_purchases')
      .select(`
        *,
        pricing:photo_upload_extension_pricing!tier_id(
          tier_name,
          display_name,
          photo_limit
        ),
        invoice:photo_upload_extension_invoices(
          id,
          invoice_number,
          invoice_type
        )
      `)
      .eq('user_id', user.id)
      .order('purchased_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      purchases: data || [],
    };
  } catch (error) {
    console.error('Error fetching user photo upload extensions:', error);
    return {
      success: false,
      error: error.message,
      purchases: [],
    };
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format datetime for display
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
